import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, DollarSign, Copy, Check, MousePointerClick, UserPlus,
  Sparkles, TrendingUp, Bell, Award, Download, Link2,
} from "lucide-react";
import { ShieldCheck, FileText, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SignInRequired } from "@/components/SignInRequired";
import { AffiliateShare } from "@/components/AffiliateShare";
import { AffiliateLinkManager } from "@/components/AffiliateLinkManager";
import { AffiliateTaxDocuments } from "@/components/AffiliateTaxDocuments";
import { fetchPayoutStatement, downloadStatementCsv, downloadStatementPdf } from "@/lib/affiliate-statement";
import { toast } from "sonner";

export const Route = createFileRoute("/affiliate/")({
  component: AffiliatePage,
  head: () => ({
    meta: [
      { title: "Creator Affiliate Program - Inquira" },
      { name: "description", content: "Earn 15% recurring commission for every Inquira Premium subscriber you refer." },
      { property: "og:title", content: "Inquira Creator Affiliate Program" },
      { property: "og:description", content: "Earn 15% recurring commission for every Inquira Premium subscriber you refer." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Dashboard = {
  ok: boolean;
  error?: string;
  settings?: { minimum_payout_cents: number; payout_frequency: string; hold_period_days: number };
  affiliate: {
    id: string; status: string; creator_name: string | null; affiliate_code: string | null;
    slug: string; coupon_code: string | null; commission_pct: number | null;
    payout_method: string | null; payout_email: string | null; tax_status: string | null;
    verification_status: string | null; country: string | null; featured: boolean | null;
    approved_at: string | null;
  } | null;
  totals?: {
    clicks: number; unique_visitors: number; signups: number; trials: number;
    paid_conversions: number; active_subscribers: number; cancelled_subscribers: number;
  };
  balances?: {
    pending_cents: number; approved_cents: number; available_cents: number;
    paid_cents: number; reversed_cents: number; lifetime_cents: number; mrr_cents: number;
  };
  series?: Array<{ day: string; clicks: number; signups: number; trials: number; conversions: number; commission_cents: number; revenue_cents: number }>;
  commissions?: Array<{ id: string; billing_platform: string; commission_amount: number; eligible_net_amount: number; currency: string; status: string; hold_until: string | null; created_at: string; paid_at: string | null }>;
  payouts?: Array<{ id: string; amount_cents: number; currency: string; method: string | null; status: string; transaction_reference: string | null; paid_at: string | null; created_at: string }>;
  milestones?: Array<{ milestone_key: string; label: string; achieved_at: string; reward: string | null }>;
  notifications?: Array<{ id: string; event_type: string; title: string; body: string | null; read_at: string | null; created_at: string }>;
  assets?: Array<{ id: string; title: string; category: string | null; asset_type: string; url: string | null; body: string | null; thumbnail_url: string | null; dimensions: string | null }>;
};

const fmtCents = (c: number) => `$${((c ?? 0) / 100).toFixed(2)}`;

type Compliance = {
  tax_status: string | null;
  verification_status: string | null;
  tax_legal_name: string | null;
  tax_entity_type: string | null;
  tax_country: string | null;
  tax_form_type: string | null;
  tax_id_last4: string | null;
  tax_submitted_at: string | null;
  verification_method: string | null;
  verification_submitted_at: string | null;
  compliance_note: string | null;
};

const COMPLIANCE_LABEL: Record<string, { text: string; cls: string }> = {
  required: { text: "Required", cls: "bg-destructive/15 text-destructive" },
  pending: { text: "Pending review", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  approved: { text: "Approved", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  verified: { text: "Approved", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  suspended: { text: "Suspended", cls: "bg-destructive/15 text-destructive" },
};

function StatusPill({ value }: { value: string | null }) {
  const key = (value ?? "required").toLowerCase();
  const meta = COMPLIANCE_LABEL[key] ?? { text: value ?? "Required", cls: "bg-muted text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>{meta.text}</span>;
}
const fmtAmount = (n: number) => `$${(n ?? 0).toFixed(2)}`;

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-border bg-card p-5 ${className}`}>{children}</div>;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg border border-border bg-background p-2 text-xs">{value}</code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="rounded-lg border border-border p-2"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-4 w-4 text-neon-green" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function AffiliatePage() {
  const { user, isLoading } = useAuth();
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [tab, setTab] = useState<"overview" | "commissions" | "payouts" | "assets">("overview");

  // Application form
  const [creatorName, setCreatorName] = useState("");
  const [slug, setSlug] = useState("");
  const [payoutEmail, setPayoutEmail] = useState("");
  const [note, setNote] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Payout email editing
  const [editingPayout, setEditingPayout] = useState(false);
  const [newPayoutEmail, setNewPayoutEmail] = useState("");
  const [savingPayout, setSavingPayout] = useState(false);

  // Tax / identity compliance
  const [compliance, setCompliance] = useState<Compliance | null>(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  const [taxForm, setTaxForm] = useState({
    tax_legal_name: "",
    tax_entity_type: "individual",
    tax_country: "",
    tax_form_type: "w9",
    tax_id_last4: "",
    verification_method: "government_id",
  });
  const [statementBusy, setStatementBusy] = useState<string | null>(null);

  const loadCompliance = useCallback(async () => {
    const { data: row } = await supabase
      .from("affiliates")
      .select(
        "tax_status, verification_status, tax_legal_name, tax_entity_type, tax_country, tax_form_type, tax_id_last4, tax_submitted_at, verification_method, verification_submitted_at, compliance_note",
      )
      .maybeSingle();
    if (!row) return;
    const c = row as unknown as Compliance;
    setCompliance(c);
    setTaxForm((f) => ({
      tax_legal_name: c.tax_legal_name ?? f.tax_legal_name,
      tax_entity_type: c.tax_entity_type ?? f.tax_entity_type,
      tax_country: c.tax_country ?? f.tax_country,
      tax_form_type: c.tax_form_type ?? f.tax_form_type,
      tax_id_last4: c.tax_id_last4 ?? "",
      verification_method: c.verification_method ?? f.verification_method,
    }));
  }, []);

  async function submitTax() {
    if (taxForm.tax_legal_name.trim().length < 2) return toast.error("Enter your legal name");
    if (taxForm.tax_country.trim().length < 2) return toast.error("Enter your tax residence country");
    setSavingTax(true);
    const { data: res, error } = await supabase.rpc("update_my_affiliate_compliance" as never, {
      _payload: { ...taxForm, tax_country: taxForm.tax_country.trim().toUpperCase() },
    } as never);
    setSavingTax(false);
    if (error) return toast.error(error.message);
    if (!(res as { ok?: boolean } | null)?.ok) return toast.error("Could not save your details");
    toast.success("Submitted for review");
    setTaxOpen(false);
    void loadCompliance();
  }

  async function openStatement(payoutId: string, kind: "pdf" | "csv") {
    setStatementBusy(payoutId + kind);
    const s = await fetchPayoutStatement(payoutId);
    if (!s.ok || !s.payout) {
      setStatementBusy(null);
      return toast.error("Statement isn't available yet");
    }
    try {
      if (kind === "csv") downloadStatementCsv(s);
      else await downloadStatementPdf(s);
    } catch {
      toast.error("Could not generate the statement");
    } finally {
      setStatementBusy(null);
    }
  }

  const load = useCallback(async (days: number) => {
    setLoading(true);
    const from = new Date(Date.now() - days * 86_400_000).toISOString();
    const { data: res, error } = await supabase.rpc("my_affiliate_dashboard" as never, {
      _from: from,
    } as never);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      setData({ ok: false, affiliate: null });
      return;
    }
    const parsed = res as unknown as Dashboard | null;
    setData(parsed && typeof parsed === "object" ? parsed : { ok: true, affiliate: null });
  }, []);

  useEffect(() => {
    if (!user) return;
    void load(range);
    void loadCompliance();
  }, [user, range, load, loadCompliance]);

  async function apply() {
    if (!/^[a-z0-9-]{3,30}$/.test(slug)) return toast.error("Link handle must be 3-30 chars: a-z, 0-9, -");
    if (!payoutEmail.includes("@")) return toast.error("Enter a valid payout email");
    if (!agreed) return toast.error("Please accept the affiliate terms");
    setSubmitting(true);
    const { data: res, error } = await supabase.rpc("apply_for_affiliate" as never, {
      _slug: slug,
      _payout_email: payoutEmail,
      _note: [creatorName && `Creator: ${creatorName}`, note].filter(Boolean).join(" — "),
    } as never);
    setSubmitting(false);
    if (error) return toast.error(error.message);
    const out = res as { ok: boolean; error?: string };
    if (!out?.ok) return toast.error(out?.error ?? "Failed to submit");
    toast.success("Application submitted — we review within 2 business days");
    void load(range);
  }

  async function savePayout() {
    if (!newPayoutEmail.includes("@")) return toast.error("Enter a valid payout email");
    setSavingPayout(true);
    const { data: res, error } = await supabase.rpc("update_my_affiliate_payout" as never, {
      _payout_email: newPayoutEmail,
    } as never);
    setSavingPayout(false);
    if (error) return toast.error(error.message);
    const out = res as { ok: boolean; error?: string };
    if (!out?.ok) return toast.error(out?.error ?? "Failed");
    toast.success("Payout email updated");
    setEditingPayout(false);
    void load(range);
  }

  const aff = data?.affiliate ?? null;
  const origin = typeof window === "undefined" ? "https://inquira.online" : window.location.origin;
  const referralLink = aff ? `${origin}/affiliate/${aff.affiliate_code ?? aff.slug}` : "";
  const rate = aff?.commission_pct ?? 15;

  const chart = useMemo(
    () =>
      (data?.series ?? []).map((p) => ({
        day: new Date(p.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        clicks: p.clicks,
        signups: p.signups,
        commission: (p.commission_cents ?? 0) / 100,
      })),
    [data?.series],
  );

  function exportCommissions() {
    const rows = data?.commissions ?? [];
    const header = "created_at,platform,status,net_revenue,commission,currency,hold_until,paid_at";
    const csv = [
      header,
      ...rows.map((c) =>
        [c.created_at, c.billing_platform, c.status, c.eligible_net_amount, c.commission_amount, c.currency, c.hold_until ?? "", c.paid_at ?? ""].join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `inquira-commissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (!user)
    return (
      <SignInRequired
        title="Sign in to join the creator program"
        description="Earn 15% recurring commission for every Inquira Premium subscriber you refer."
      />
    );

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Home
      </Link>
      <h1 className="mb-2 text-3xl font-bold">Creator Affiliate Program</h1>
      <p className="mb-6 text-muted-foreground">
        Earn <strong className="text-neon-pink">{rate}% recurring commission</strong> on eligible Inquira
        Premium revenue. Commissions clear after a {data?.settings?.hold_period_days ?? 30}-day hold and pay out{" "}
        {data?.settings?.payout_frequency ?? "monthly"} once your available balance reaches{" "}
        {fmtCents(data?.settings?.minimum_payout_cents ?? 2500)}. Balances below that carry forward automatically.
      </p>

      {loading && !data ? (
        <Card>Loading your dashboard…</Card>
      ) : !aff ? (
        <Card>
          <h2 className="mb-1 text-lg font-semibold">Apply to become a creator partner</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Tell us who you are and how you'll share Inquira. Approved partners get a tracked link, a coupon code,
            marketing assets, and a live earnings dashboard. Prefer the full form?{" "}
            <Link to="/affiliates" className="text-primary underline">
              Apply on the creator page
            </Link>
            .
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm">Creator / channel name</span>
              <input
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                placeholder="Jane Studies"
                className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">Link handle</span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="jane-studies"
                className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm">Payout email (PayPal)</span>
              <input
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                type="email"
                className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm">Where and how will you promote Inquira?</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="TikTok study content, 40k followers, weekly homework help videos…"
                className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
              />
            </label>
          </div>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" />
            <span>
              I agree to the affiliate terms: no self-referrals, no paid brand-name bidding, no coupon-site spam, and
              commissions may be reversed on refunds or chargebacks. See our{" "}
              <Link to="/terms" className="text-primary underline">
                Terms
              </Link>
              .
            </span>
          </label>
          <button
            disabled={submitting}
            onClick={apply}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Status + links */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm text-muted-foreground">Partner status</div>
                <div className="text-lg font-semibold capitalize">
                  {aff.status}
                  {aff.featured ? " · Featured" : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Your rate</div>
                <div className="text-lg font-semibold text-neon-pink">{rate}%</div>
              </div>
            </div>

            {aff.status === "approved" ? (
              <div className="mt-4 grid gap-3">
                <CopyField label="Referral link" value={referralLink} />
                {aff.coupon_code && <CopyField label="Coupon code" value={aff.coupon_code} />}
                {aff.affiliate_code && <CopyField label="Affiliate code" value={aff.affiliate_code} />}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {aff.status === "pending"
                  ? "Your application is under review. We'll email you once it's approved."
                  : `Your application status is "${aff.status}". Contact info@inquira.online with questions.`}
              </p>
            )}

            <div className="mt-4">
              <div className="text-sm text-muted-foreground">Payout email</div>
              {editingPayout ? (
                <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                  <input
                    value={newPayoutEmail}
                    onChange={(e) => setNewPayoutEmail(e.target.value)}
                    type="email"
                    className="flex-1 rounded-lg border border-border bg-background p-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      disabled={savingPayout}
                      onClick={savePayout}
                      className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {savingPayout ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => setEditingPayout(false)} className="rounded-lg border border-border px-3 py-2 text-xs">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="text-sm">{aff.payout_email || "—"}</code>
                  <button
                    onClick={() => {
                      setNewPayoutEmail(aff.payout_email ?? "");
                      setEditingPayout(true);
                    }}
                    className="text-xs text-primary underline"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </Card>

          {aff.status === "approved" && <AffiliateShare link={referralLink} code={aff.affiliate_code ?? aff.slug} />}

          {aff.status === "approved" && <AffiliateLinkManager origin={origin} />}
          {aff.status === "approved" && <AffiliateTaxDocuments userId={user.id} />}

          {/* Tax & identity compliance */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="inline-flex items-center gap-2 text-lg font-semibold">
                <ShieldCheck className="h-4 w-4 text-neon-pink" /> Tax &amp; identity
              </h3>
              <div className="flex items-center gap-2">
                <Link
                  to="/affiliate/analytics"
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium"
                >
                  <BarChart3 className="h-3.5 w-3.5" /> Analytics
                </Link>
                <Link
                  to="/affiliate/payouts"
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium"
                >
                  <DollarSign className="h-3.5 w-3.5" /> Payout status
                </Link>
                <button
                  onClick={() => setTaxOpen((v) => !v)}
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
                >
                  {taxOpen ? "Close" : compliance?.tax_submitted_at ? "Update details" : "Submit tax info"}
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <div className="flex items-center justify-between gap-2 rounded-xl border border-border p-3">
                <span className="text-muted-foreground">Tax information</span>
                <StatusPill value={compliance?.tax_status ?? aff.tax_status} />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl border border-border p-3">
                <span className="text-muted-foreground">Identity verification</span>
                <StatusPill value={compliance?.verification_status ?? aff.verification_status} />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Both must be approved before a payout can be released. We store only your entity details and the last 4
              digits of your tax ID.
              {compliance?.tax_id_last4 ? ` Tax ID on file ends in ${compliance.tax_id_last4}.` : ""}
            </p>
            {compliance?.compliance_note && (
              <p className="mt-2 rounded-lg border border-border p-3 text-xs">
                <span className="font-medium">Reviewer note:</span> {compliance.compliance_note}
              </p>
            )}

            {taxOpen && (
              <div className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm">Legal name</span>
                  <input
                    value={taxForm.tax_legal_name}
                    onChange={(e) => setTaxForm((f) => ({ ...f, tax_legal_name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-sm">Entity type</span>
                  <select
                    value={taxForm.tax_entity_type}
                    onChange={(e) => setTaxForm((f) => ({ ...f, tax_entity_type: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  >
                    <option value="individual">Individual / sole proprietor</option>
                    <option value="company">Company</option>
                    <option value="partnership">Partnership</option>
                    <option value="nonprofit">Non-profit</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm">Tax residence country</span>
                  <input
                    value={taxForm.tax_country}
                    onChange={(e) => setTaxForm((f) => ({ ...f, tax_country: e.target.value }))}
                    placeholder="US"
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-sm">Tax form</span>
                  <select
                    value={taxForm.tax_form_type}
                    onChange={(e) => setTaxForm((f) => ({ ...f, tax_form_type: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  >
                    <option value="w9">W-9 (US person)</option>
                    <option value="w8ben">W-8BEN (non-US individual)</option>
                    <option value="w8bene">W-8BEN-E (non-US entity)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm">Last 4 of tax ID</span>
                  <input
                    value={taxForm.tax_id_last4}
                    onChange={(e) => setTaxForm((f) => ({ ...f, tax_id_last4: e.target.value }))}
                    maxLength={4}
                    inputMode="numeric"
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-sm">Identity verification method</span>
                  <select
                    value={taxForm.verification_method}
                    onChange={(e) => setTaxForm((f) => ({ ...f, verification_method: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
                  >
                    <option value="government_id">Government ID</option>
                    <option value="stripe_identity">Stripe Identity</option>
                    <option value="business_registration">Business registration</option>
                  </select>
                </label>
                <button
                  disabled={savingTax}
                  onClick={submitTax}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50 sm:col-span-2"
                >
                  {savingTax ? "Submitting…" : "Submit for review"}
                </button>
              </div>
            )}
          </Card>

          {/* Balances */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Available", val: data?.balances?.available_cents ?? 0 },
              { label: "Pending", val: data?.balances?.pending_cents ?? 0 },
              { label: "Paid out", val: data?.balances?.paid_cents ?? 0 },
              { label: "Last 30d", val: data?.balances?.mrr_cents ?? 0 },
            ].map((s) => (
              <Card key={s.label} className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <DollarSign className="h-3 w-3" /> {s.label}
                </div>
                <div className="mt-1 text-xl font-bold">{fmtCents(s.val)}</div>
              </Card>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            {(["overview", "commissions", "payouts", "assets"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`rounded-full px-4 py-1.5 text-sm capitalize ${
                  tab === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"
                }`}
              >
                {t}
              </button>
            ))}
            <div className="ml-auto flex gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setRange(d)}
                  className={`rounded-full px-3 py-1.5 text-xs ${
                    range === d ? "bg-secondary text-secondary-foreground" : "border border-border text-muted-foreground"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {tab === "overview" && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: "Clicks", val: data?.totals?.clicks ?? 0, Icon: MousePointerClick },
                  { label: "Signups", val: data?.totals?.signups ?? 0, Icon: UserPlus },
                  { label: "Trials", val: data?.totals?.trials ?? 0, Icon: Sparkles },
                  { label: "Active subs", val: data?.totals?.active_subscribers ?? 0, Icon: TrendingUp },
                ].map(({ label, val, Icon }) => (
                  <Card key={label} className="p-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon className="h-3 w-3" /> {label}
                    </div>
                    <div className="mt-1 text-xl font-bold">{val}</div>
                  </Card>
                ))}
              </div>

              <Card>
                <h3 className="mb-3 text-lg font-semibold">Performance</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 12,
                          fontSize: 12,
                        }}
                      />
                      <Area type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="signups" stroke="hsl(var(--secondary))" fill="hsl(var(--secondary))" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="commission" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {(data?.milestones?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                    <Award className="h-4 w-4 text-neon-yellow" /> Milestones
                  </h3>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {data!.milestones!.map((m) => (
                      <li key={m.milestone_key} className="rounded-xl border border-border p-3 text-sm">
                        <div className="font-medium">{m.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(m.achieved_at).toLocaleDateString()}
                          {m.reward ? ` · ${m.reward}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {(data?.notifications?.length ?? 0) > 0 && (
                <Card>
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                    <Bell className="h-4 w-4" /> Updates
                  </h3>
                  <ul className="divide-y divide-border text-sm">
                    {data!.notifications!.slice(0, 8).map((n) => (
                      <li key={n.id} className="py-2">
                        <div className="font-medium">{n.title}</div>
                        {n.body && <div className="text-xs text-muted-foreground">{n.body}</div>}
                        <div className="text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </>
          )}

          {tab === "commissions" && (
            <Card>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Commissions</h3>
                <button
                  onClick={exportCommissions}
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs"
                >
                  <Download className="h-3 w-3" /> CSV
                </button>
              </div>
              {(data?.commissions?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No commissions yet. Share your link to get started.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="py-2">Date</th>
                        <th className="py-2">Revenue</th>
                        <th className="py-2">Commission</th>
                        <th className="py-2">Status</th>
                        <th className="py-2">Clears</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data!.commissions!.map((c) => (
                        <tr key={c.id}>
                          <td className="py-2">{new Date(c.created_at).toLocaleDateString()}</td>
                          <td className="py-2">{fmtAmount(Number(c.eligible_net_amount))}</td>
                          <td className="py-2 font-medium">{fmtAmount(Number(c.commission_amount))}</td>
                          <td className="py-2 capitalize">{c.status}</td>
                          <td className="py-2 text-xs text-muted-foreground">
                            {c.hold_until ? new Date(c.hold_until).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {tab === "payouts" && (
            <Card>
              <h3 className="mb-3 text-lg font-semibold">Payouts</h3>
              {(data?.payouts?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No payouts yet. Balances are paid {data?.settings?.payout_frequency ?? "monthly"} once available
                  earnings reach {fmtCents(data?.settings?.minimum_payout_cents ?? 2500)}. Anything below that carries
                  forward to the next payout period.
                </p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {data!.payouts!.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                      <span>{new Date(p.created_at).toLocaleDateString()}</span>
                      <span className="capitalize text-muted-foreground">
                        {p.method ?? "paypal"} · {p.status}
                      </span>
                      <span className="font-medium">{fmtCents(p.amount_cents)}</span>
                      {p.transaction_reference && (
                        <code className="text-[11px] text-muted-foreground">{p.transaction_reference}</code>
                      )}
                      <span className="flex items-center gap-2">
                        <Link
                          to="/affiliate/statement/$id"
                          params={{ id: p.id }}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[11px]"
                        >
                          <FileText className="h-3 w-3" /> Statement
                        </Link>
                        <button
                          disabled={statementBusy === p.id + "pdf"}
                          onClick={() => void openStatement(p.id, "pdf")}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[11px] disabled:opacity-50"
                        >
                          <Download className="h-3 w-3" /> PDF
                        </button>
                        <button
                          disabled={statementBusy === p.id + "csv"}
                          onClick={() => void openStatement(p.id, "csv")}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[11px] disabled:opacity-50"
                        >
                          <Download className="h-3 w-3" /> CSV
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === "assets" && (
            <Card>
              <h3 className="mb-1 text-lg font-semibold">Marketing assets</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Approved copy, banners, and video hooks. Always disclose the partnership.
              </p>
              {(data?.assets?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">Assets are being prepared — check back soon.</p>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {data!.assets!.map((a) => (
                    <li key={a.id} className="rounded-xl border border-border p-3">
                      <div className="text-sm font-medium">{a.title}</div>
                      <div className="mb-2 text-xs text-muted-foreground">
                        {a.category ?? a.asset_type}
                        {a.dimensions ? ` · ${a.dimensions}` : ""}
                      </div>
                      {a.thumbnail_url && (
                        <img src={a.thumbnail_url} alt={a.title} loading="lazy" className="mb-2 w-full rounded-lg" />
                      )}
                      {a.body && <p className="mb-2 whitespace-pre-wrap text-xs">{a.body}</p>}
                      {a.url && (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary underline"
                        >
                          <Link2 className="h-3 w-3" /> Open asset
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
